import { enrichApiErrorScope } from '@hub/lib/api-error/enrich-api-error-scope';
import { enrichRequestScope } from '@hub/lib/api-error/enrich-request-scope';
import { RecaptchaError } from '@hub/lib/recaptcha';
import type { SentryScopeTags } from '@hub/lib/sentry-scope';
import { m } from '@hub/messages';
import type { NavigateFn } from '@hub/router';
// Direct sibling import — going through the `@hub/router` barrel would close a
// runtime cycle (queryClient → @hub/router → ./router → @hub/app/queryClient).
// Today's barrel order makes that cycle safe by accident; the direct import
// removes the trap.
import { RouterPaths } from '@hub/router/routePaths';
import { ApiError, isNetworkError, isTransientAuthError, TimeoutError } from '@platform/api';
import { captureException, withScope } from '@platform/observability';
import { track } from '@platform/tracking';
import { MutationCache, QueryCache, QueryClient, type Register } from '@tanstack/react-query';
import { toast } from 'sonner';
import { enrichSentryScope } from './bootstrap/enrichSentryScope';
import './commons/tracking';
import { ERROR_TOAST_DURATION } from './error-boundary/constants';
import { ErrorToastDescription } from './error-boundary/ErrorToastDescription';
import { retryPolicy } from './retryPolicy';

let _navigate: NavigateFn | undefined;

export function registerErrorDeps(deps: { navigate: NavigateFn }) {
  _navigate = deps.navigate;
}

export function handleGlobalError(error: unknown, meta?: Register['queryMeta']) {
  // See the `errorHandling` doc in global.d.ts. 'self-reported' callers already capture via
  // extractApiErrorCode — reporting here too would double-report the same error.
  if (meta?.errorHandling === 'self-reported') {
    return;
  }
  const notifyUser = meta?.errorHandling === undefined;

  // Every capture below carries the caller's flow, so one enriched event says which feature broke.
  // A second capture just to add the tag would either duplicate the event or lose to Dedupe.
  const tagFlow = (scope: SentryScopeTags) => {
    if (meta?.flow) {
      scope.setTag('flow', meta.flow);
    }
  };

  if (error instanceof ApiError) {
    switch (error.status) {
      case 403:
        if (notifyUser) {
          void _navigate?.({ to: RouterPaths.forbidden.path });
        }
        return;
      default:
        if (error.status >= 500) {
          let eventId = '';
          withScope((scope) => {
            tagFlow(scope);
            enrichApiErrorScope(error, scope);
            eventId = captureException(error);
          });
          if (notifyUser) {
            // Sonner updates a toast with a matching id in place, so a page whose requests all
            // fail shows one toast per error class instead of one per request.
            toast.error(m.commons_error_server(), {
              id: 'error-server',
              description: <ErrorToastDescription eventId={eventId} />,
              duration: ERROR_TOAST_DURATION,
            });
            track('error_toast_shown', { error_type: 'server', status_code: error.status });
          }
        }
        return;
    }
  }

  if (error instanceof TimeoutError) {
    // A timeout the client caused itself carries `sentryIgnore`, so beforeSend drops it. No guard here.
    withScope((scope) => {
      tagFlow(scope);
      enrichRequestScope(error, scope);
      captureException(error);
    });
    if (notifyUser) {
      toast.error(m.commons_error_timeout(), { id: 'error-timeout', duration: ERROR_TOAST_DURATION });
      track('error_toast_shown', { error_type: 'timeout' });
    }
    return;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return;
  }

  if (isNetworkError(error)) {
    if (notifyUser) {
      toast.error(m.commons_error_network(), { id: 'error-network', duration: ERROR_TOAST_DURATION });
      track('error_toast_shown', { error_type: 'network' });
    }
    return;
  }

  if (isTransientAuthError(error)) {
    // Retries (see queryClient.retry) already ran; a sustained IdP blip is a network-class problem,
    // not a crash. The error carries `sentryIgnore`, so no capture site reports it.
    if (notifyUser) {
      toast.error(m.commons_error_network(), { id: 'error-network', duration: ERROR_TOAST_DURATION });
      track('error_toast_shown', { error_type: 'transient_auth' });
    }
    return;
  }

  // Already captured with flow/action tags in useRecaptchaMutation. Only the client-side class, so the
  // BFF's own 400/403 rejection still gets reported here.
  if (error instanceof RecaptchaError) {
    return;
  }

  // Catch-all: capture unrecognized errors (e.g. ZodError from a response validator or query select).
  // A bare TypeError lands here on purpose: real transport failures are classified as NetworkError
  // (fetch-level in timeoutFetch, body-stream death in the error interceptor), so a TypeError that
  // reaches here is a logic bug in our own code — captured, not masked as flaky network.
  withScope((scope) => {
    tagFlow(scope);
    enrichSentryScope(error, scope);
    captureException(error);
  });
}

export const STALE_TIME_MS = 30 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryPolicy,
      staleTime: STALE_TIME_MS,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => handleGlobalError(error, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => handleGlobalError(error, mutation.meta),
  }),
});
